/**
 * agentActivity.store — REST-based agent activity tracking (Zustand).
 *
 * When WebSocket is unavailable, the right panel has no real-time signal of
 * which agents are working. This store provides a fallback: whenever the REST
 * `sendChatMessage` call returns with a `role` field, the `useChat` hook calls
 * `markActive(agentId)` here. The agent shows as "active" for a short window
 * (ACTIVITY_WINDOW_MS) and then reverts to idle.
 *
 * The RightPanel merges this store's state with `useChatStream`: if WS is live
 * and already reporting activity, it takes precedence; this store only fills
 * the gap when WS agents are all idle.
 */
import { create } from 'zustand';
import type { AgentId } from '@/tokens/colors';

const ACTIVITY_WINDOW_MS = 60_000;

export interface AgentActivityEntry {
  readonly agentId: AgentId;
  /** Epoch ms when the agent was last marked active via REST response. */
  readonly activeSince: number;
}

export interface AgentActivityState {
  /** Map of agent id to when it was last marked active. */
  readonly activeAgents: Partial<Record<AgentId, AgentActivityEntry>>;
  /** Mark an agent as "recently active" for ACTIVITY_WINDOW_MS. */
  markActive: (agentId: AgentId) => void;
  /** Clear a specific agent (called by the expiry timer). */
  markIdle: (agentId: AgentId) => void;
  /** Reset all activity (e.g., on session switch). */
  reset: () => void;
}

/** Timer handles for per-agent expiry. Not part of the Zustand state. */
const expiryTimers: Partial<Record<AgentId, ReturnType<typeof setTimeout>>> = {};

export const useAgentActivity = create<AgentActivityState>((set, get) => ({
  activeAgents: {},

  markActive: (agentId) => {
    const now = Date.now();
    const entry: AgentActivityEntry = { agentId, activeSince: now };
    set((state) => ({
      activeAgents: { ...state.activeAgents, [agentId]: entry },
    }));

    // Clear any existing timer for this agent, then set a new one.
    const existing = expiryTimers[agentId];
    if (existing !== undefined) {
      clearTimeout(existing);
    }

    expiryTimers[agentId] = setTimeout(() => {
      get().markIdle(agentId);
       
      delete expiryTimers[agentId];
    }, ACTIVITY_WINDOW_MS);
  },

  markIdle: (agentId) => {
    set((state) => {
      const next = { ...state.activeAgents };
       
      delete next[agentId];
      return { activeAgents: next };
    });
  },

  reset: () => {
    // Clear all pending timers.
    for (const id of Object.keys(expiryTimers) as AgentId[]) {
      const timer = expiryTimers[id];
      if (timer !== undefined) {
        clearTimeout(timer);
      }
       
      delete expiryTimers[id];
    }
    set({ activeAgents: {} });
  },
}));
